import type { RulesPatch, UserRules } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { RotateCcwIcon } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMinutes } from '@/lib/format'
import { queryKeys, useRunTool, useSettings } from '@/lib/items'
import { choicesFor, DEFAULTS_PATCH, ESTIMATE_RULES, isCustom, RULE_CHOICES } from '@/lib/rules'
import { PriorityRulesSection } from './priority-rules-section'

const days = (n: number) => (n === 1 ? '1 day' : `${n} days`)
const workingDays = (n: number) => (n === 1 ? '1 working day' : `${n} working days`)

/**
 * Settings → Rules: the numbers hugo decides with, each the user's to change. A change saves at
 * once and hugo replans with it; "Back to hugo's defaults" undoes them all.
 */
export function RulesCard() {
  const settings = useSettings()
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const [saving, setSaving] = useState(false)
  const rules = settings.data?.rules

  const save = async (patch: RulesPatch, done: string) => {
    setSaving(true)
    const result = await run(
      'settings.update',
      { rules: patch },
      { done, failed: 'Could not change the rule' },
    )
    if (result) {
      queryClient.setQueryData(queryKeys.settings, result)
      // The plan, the board and the heads-ups follow the rules.
      await queryClient.invalidateQueries({ queryKey: queryKeys.items })
      await queryClient.invalidateQueries({ queryKey: queryKeys.timeOff })
    }
    setSaving(false)
  }

  return (
    <Card data-testid="rules-settings">
      <CardHeader>
        <CardTitle>Rules</CardTitle>
        <CardDescription>
          The numbers hugo plans and reminds with. Change one and hugo replans right away.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {!rules ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <section className="flex flex-col gap-2" aria-labelledby="rules-estimates">
              <h3 id="rules-estimates" className="text-sm font-medium">
                Time needed when you haven’t said
              </h3>
              <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                {ESTIMATE_RULES.map(({ key, label }) => (
                  <RuleSelect
                    key={key}
                    id={`rule-estimate-${key}`}
                    label={label}
                    value={rules.estimateMinutes[key]}
                    choices={RULE_CHOICES.estimateMinutes}
                    format={formatMinutes}
                    disabled={saving}
                    onChange={(value) =>
                      void save(
                        { estimateMinutes: { [key]: value } },
                        `${label}: ${formatMinutes(value)}`,
                      )
                    }
                  />
                ))}
              </div>
            </section>

            <section className="flex flex-col gap-2" aria-labelledby="rules-reminders">
              <h3 id="rules-reminders" className="text-sm font-medium">
                Reminders and time off
              </h3>
              <NumberRule
                rules={rules}
                rule="reminderIntervalWorkingDays"
                label="Next reminder after"
                format={workingDays}
                disabled={saving}
                save={save}
              />
              <NumberRule
                rules={rules}
                rule="awaySoonDays"
                label="Show “Away from …” before time off"
                format={(n) => (n === 0 ? 'Only while away' : days(n))}
                disabled={saving}
                save={save}
              />
              <NumberRule
                rules={rules}
                rule="timeOffNoticeDays"
                label="Tell people you wait on about time off"
                format={(n) => `${days(n)} ahead`}
                disabled={saving}
                save={save}
              />
              <NumberRule
                rules={rules}
                rule="undatedNoticeWorkingDays"
                label="…or, without a follow-up date"
                format={(n) => `${workingDays(n)} ahead`}
                disabled={saving}
                save={save}
              />
            </section>

            <section className="flex flex-col gap-2" aria-labelledby="rules-focus">
              <h3 id="rules-focus" className="text-sm font-medium">
                Focus time
              </h3>
              <NumberRule
                rules={rules}
                rule="focusBlockMinutes"
                label="Shortest free stretch that counts"
                format={formatMinutes}
                disabled={saving}
                save={save}
              />
            </section>

            <PriorityRulesSection rules={rules.priorityRules} disabled={saving} save={save} />

            {isCustom(rules) ? (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  onClick={() => void save(DEFAULTS_PATCH, 'Rules back to hugo’s defaults')}
                >
                  <RotateCcwIcon aria-hidden="true" />
                  Back to hugo’s defaults
                </Button>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

type NumberRuleKey = Exclude<keyof UserRules, 'estimateMinutes' | 'priorityRules'>

function NumberRule({
  rules,
  rule,
  label,
  format,
  disabled,
  save,
}: {
  rules: UserRules
  rule: NumberRuleKey
  label: string
  format: (value: number) => string
  disabled: boolean
  save: (patch: RulesPatch, done: string) => Promise<void>
}) {
  return (
    <RuleSelect
      id={`rule-${rule}`}
      label={label}
      value={rules[rule]}
      choices={RULE_CHOICES[rule]}
      format={format}
      disabled={disabled}
      onChange={(value) => void save({ [rule]: value }, `${label}: ${format(value)}`)}
    />
  )
}

function RuleSelect({
  id,
  label,
  value,
  choices,
  format,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: number
  choices: readonly number[]
  format: (value: number) => string
  disabled: boolean
  onChange: (value: number) => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
      <NativeSelect
        id={id}
        size="sm"
        className="w-48 max-w-full"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {choicesFor(choices, value).map((choice) => (
          <NativeSelectOption key={choice} value={choice}>
            {format(choice)}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </div>
  )
}
